import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Phone, Search, Trash2, CheckCircle, AlertCircle, CreditCard, ShieldCheck, Zap } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';
import Logo from './Logo';
import CallForwardingSetup from './CallForwardingSetup';

import { BACKEND_URL } from "../lib/backendUrl";
const API = `${BACKEND_URL}/api`;

const PhoneSetup = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [activeTab, setActiveTab] = useState('buy');
  const [loading, setLoading] = useState(false);
  const [myNumbers, setMyNumbers] = useState([]);
  const [availableNumbers, setAvailableNumbers] = useState([]);
  const [searchAreaCode, setSearchAreaCode] = useState('');
  const [existingNumber, setExistingNumber] = useState('');
  const [existingNumberName, setExistingNumberName] = useState('');
  const [subStatus, setSubStatus] = useState(null);
  const [provisionLoading, setProvisionLoading] = useState(null);
  const [twilioTrialLimited, setTwilioTrialLimited] = useState(false);

  useEffect(() => {
    if (token) {
      fetchMyNumbers();
      fetchSubStatus();
    }
  }, [token]);

  const fetchMyNumbers = async () => {
    try {
      const response = await axios.get(`${API}/phone/my-numbers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMyNumbers(response.data);
    } catch (error) {
      console.error('Error fetching numbers:', error);
    }
  };

  const fetchSubStatus = async () => {
    try {
      const response = await axios.get(`${API}/phone/subscription-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubStatus(response.data);
    } catch (error) {
      console.error('Error fetching subscription status:', error);
    }
  };

  const getErrorMsg = (error, fallback) => {
    const detail = error?.response?.data?.detail;
    if (!detail) return fallback;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) return detail.map(d => d.msg || d.message || JSON.stringify(d)).join('. ');
    return fallback;
  };

  const searchAvailableNumbers = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        country_code: 'US',
        ...(searchAreaCode && { area_code: searchAreaCode })
      });
      const response = await axios.get(`${API}/phone/available?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAvailableNumbers(response.data.available_numbers);
      if (response.data.count === 0) {
        toast({ title: 'No Numbers Found', description: 'Try a different area code', variant: 'destructive' });
      }
    } catch (error) {
      toast({ title: 'Search Failed', description: getErrorMsg(error, 'Failed to search numbers'), variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const provisionNumber = async (phoneNumber) => {
    setProvisionLoading(phoneNumber);
    try {
      await axios.post(
        `${API}/phone/purchase`,
        { phone_number: phoneNumber },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast({ title: 'Number Activated!', description: 'Your dedicated phone number is now ready to receive calls.' });
      fetchMyNumbers();
      fetchSubStatus();
      setAvailableNumbers([]);
    } catch (error) {
      const status = error?.response?.status;
      const detail = error?.response?.data?.detail || '';
      const isTwilioTrialLimit = typeof detail === 'string' && detail.toLowerCase().includes('trial accounts are allowed only one');
      if (status === 402) {
        toast({ title: 'Subscription Required', description: 'Please subscribe to a plan to get a dedicated phone number.', variant: 'destructive' });
      } else if (isTwilioTrialLimit) {
        toast({
          title: 'Twilio Trial Limit Reached',
          description: 'The Twilio account is on a trial plan which only allows 1 number. You can use the "Use Existing Number" tab to add your own number, or upgrade the Twilio account.',
          variant: 'destructive',
          duration: 10000,
        });
        setTwilioTrialLimited(true);
      } else {
        toast({ title: 'Activation Failed', description: getErrorMsg(error, 'Failed to activate number'), variant: 'destructive' });
      }
    } finally {
      setProvisionLoading(null);
    }
  };

  const addExistingNumber = async () => {
    if (!existingNumber.trim()) {
      toast({ title: 'Validation Error', description: 'Please enter a phone number', variant: 'destructive' });
      return;
    }
    setLoading(true);
    try {
      await axios.post(
        `${API}/phone/add-existing`,
        { phone_number: existingNumber, friendly_name: existingNumberName || existingNumber },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast({ title: 'Success!', description: 'Phone number added successfully' });
      fetchMyNumbers();
      setExistingNumber('');
      setExistingNumberName('');
    } catch (error) {
      toast({ title: 'Error', description: getErrorMsg(error, 'Failed to add number'), variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const deleteNumber = async (numberId) => {
    if (!window.confirm('Are you sure you want to remove this phone number?')) return;
    try {
      await axios.delete(`${API}/phone/${numberId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({ title: 'Removed', description: 'Phone number removed' });
      fetchMyNumbers();
      fetchSubStatus();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to remove number', variant: 'destructive' });
    }
  };

  const canProvision = subStatus?.can_provision;
  const noSubscription = subStatus?.reason === 'no_subscription';
  const limitReached = subStatus?.reason === 'limit_reached';

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a1628] via-[#0d1f35] to-[#0a1628]">
      <header className="border-b border-white/5 bg-[#0a1628]/80 backdrop-blur-lg">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Logo linkTo="/dashboard" />
            <div className="h-6 w-px bg-white/10" />
            <h1 className="text-xl font-bold text-white">Phone Number Setup</h1>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-12">
        {/* Active Numbers */}
        {myNumbers.length > 0 && (
          <Card className="bg-gradient-to-br from-teal-900/20 to-purple-900/20 border-white/10 mb-6" data-testid="my-numbers-card">
            <CardHeader>
              <CardTitle className="text-white">My Phone Numbers</CardTitle>
              <CardDescription className="text-gray-400">
                Your active phone numbers
                {subStatus && <span className="ml-2 text-purple-300">({subStatus.current_count}/{subStatus.limit} Twilio numbers used)</span>}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {myNumbers.map((number) => (
                  <div key={number.id} className="flex items-center justify-between p-4 bg-black/20 rounded-lg" data-testid={`phone-number-${number.id}`}>
                    <div className="flex items-center gap-3">
                      <Phone className="w-5 h-5 text-purple-400" />
                      <div>
                        <p className="text-white font-medium">{number.phone_number}</p>
                        <p className="text-sm text-gray-400">
                          {number.is_twilio_number ? (
                            <span className="flex items-center gap-1"><CheckCircle className="w-3 h-3 text-green-400" />Dedicated Number (Active)</span>
                          ) : (
                            <span className="flex items-center gap-1"><AlertCircle className="w-3 h-3 text-yellow-400" />Forwarding Number</span>
                          )}
                        </p>
                      </div>
                    </div>
                    <Button onClick={() => deleteNumber(number.id)} variant="outline" size="sm" className="bg-transparent border-white/10 text-red-400 hover:bg-red-500/10" data-testid={`delete-number-${number.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2 bg-black/20 border border-white/10">
            <TabsTrigger value="buy" className="data-[state=active]:bg-purple-600" data-testid="tab-get-number">
              Get Dedicated Number
            </TabsTrigger>
            <TabsTrigger value="existing" className="data-[state=active]:bg-purple-600" data-testid="tab-existing-number">
              Use Existing Number
            </TabsTrigger>
          </TabsList>

          {/* Get Dedicated Number Tab */}
          <TabsContent value="buy" className="space-y-6">
            {/* Subscription gate */}
            {noSubscription && (
              <Card className="bg-gradient-to-br from-amber-900/20 to-red-900/20 border-amber-500/30" data-testid="subscription-required-card">
                <CardContent className="py-8 text-center space-y-4">
                  <CreditCard className="w-12 h-12 text-amber-400 mx-auto" />
                  <h3 className="text-xl font-bold text-white">Subscription Required</h3>
                  <p className="text-gray-300 max-w-md mx-auto">
                    A dedicated business phone number is included with every Miadesk plan. Subscribe to get started.
                  </p>
                  <Button onClick={() => navigate('/#pricing')} className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white px-8 py-3" data-testid="go-to-pricing-btn">
                    View Plans & Subscribe
                  </Button>
                </CardContent>
              </Card>
            )}

            {limitReached && (
              <Card className="bg-gradient-to-br from-amber-900/20 to-orange-900/20 border-amber-500/30" data-testid="limit-reached-card">
                <CardContent className="py-8 text-center space-y-4">
                  <Zap className="w-12 h-12 text-amber-400 mx-auto" />
                  <h3 className="text-xl font-bold text-white">Phone Number Limit Reached</h3>
                  <p className="text-gray-300 max-w-md mx-auto">
                    You've used all {subStatus?.limit} phone number(s) included with your plan. Upgrade to get more.
                  </p>
                  <Button onClick={() => navigate('/#pricing')} className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white px-8 py-3" data-testid="upgrade-plan-btn">
                    Upgrade Plan
                  </Button>
                </CardContent>
              </Card>
            )}

            {canProvision && (
              <>
              {twilioTrialLimited && (
                <Card className="bg-gradient-to-br from-amber-900/20 to-red-900/20 border-amber-500/30 mb-4" data-testid="twilio-trial-limit-card">
                  <CardContent className="py-6 text-center space-y-3">
                    <AlertCircle className="w-10 h-10 text-amber-400 mx-auto" />
                    <h3 className="text-lg font-bold text-white">Twilio Trial Account Limit</h3>
                    <p className="text-gray-300 max-w-md mx-auto text-sm">
                      The Twilio account only allows 1 phone number on a trial plan. You can still use the <strong>"Use Existing Number"</strong> tab to add your own business number and set up call forwarding.
                    </p>
                  </CardContent>
                </Card>
              )}
              <Card className="bg-gradient-to-br from-purple-900/20 to-teal-900/20 border-white/10" data-testid="search-numbers-card">
                <CardHeader>
                  <div className="flex items-center gap-2 mb-2">
                    <ShieldCheck className="w-5 h-5 text-emerald-400" />
                    <span className="text-sm text-emerald-400 font-medium">Included with your plan — no extra charge</span>
                  </div>
                  <CardTitle className="text-white">Get Your Dedicated Number</CardTitle>
                  <CardDescription className="text-gray-400">
                    Search for an available number and activate it instantly. This number is included with your subscription.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex gap-2">
                    <div className="flex-1">
                      <Label htmlFor="areaCode" className="text-gray-200 mb-2 block">Area Code (Optional)</Label>
                      <Input
                        id="areaCode"
                        value={searchAreaCode}
                        onChange={(e) => setSearchAreaCode(e.target.value)}
                        placeholder="e.g., 415, 212, 310"
                        className="bg-black/30 border-white/10 text-white placeholder:text-gray-500"
                        data-testid="area-code-input"
                      />
                    </div>
                    <Button
                      onClick={searchAvailableNumbers}
                      disabled={loading}
                      className="mt-8 bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-700 hover:to-purple-600 text-white"
                      data-testid="search-numbers-btn"
                    >
                      <Search className="w-4 h-4 mr-2" />
                      {loading ? 'Searching...' : 'Search'}
                    </Button>
                  </div>

                  {availableNumbers.length > 0 && (
                    <div className="space-y-2 mt-6" data-testid="available-numbers-list">
                      <h3 className="text-white font-medium mb-3">Available Numbers:</h3>
                      {availableNumbers.map((number, index) => (
                        <div key={index} className="flex items-center justify-between p-4 bg-black/30 rounded-lg hover:bg-black/40 transition-colors" data-testid={`available-number-${index}`}>
                          <div>
                            <p className="text-white font-medium">{number.phone_number}</p>
                            <p className="text-sm text-gray-400">{number.locality}, {number.region}</p>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="text-xs text-emerald-400 font-medium bg-emerald-400/10 px-2 py-1 rounded-full border border-emerald-400/20">
                              Included
                            </span>
                            <Button
                              onClick={() => provisionNumber(number.phone_number)}
                              disabled={provisionLoading === number.phone_number}
                              size="sm"
                              className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white"
                              data-testid={`activate-number-${index}`}
                            >
                              <CheckCircle className="w-4 h-4 mr-1" />
                              {provisionLoading === number.phone_number ? 'Activating...' : 'Activate'}
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
              </>
            )}

            {/* Info when subStatus hasn't loaded yet */}
            {!subStatus && (
              <div className="text-center py-12 text-gray-400">Loading subscription status...</div>
            )}
          </TabsContent>

          {/* Use Existing Number Tab */}
          <TabsContent value="existing" className="space-y-6">
            {/* Gate: Must have a dedicated number first */}
            {!myNumbers.some(n => n.is_twilio_number) ? (
              <Card className="bg-gradient-to-br from-amber-900/20 to-purple-900/20 border-amber-500/30" data-testid="dedicated-number-required-card">
                <CardContent className="py-10 text-center space-y-4">
                  <Phone className="w-12 h-12 text-amber-400 mx-auto" />
                  <h3 className="text-xl font-bold text-white">Dedicated Number Required</h3>
                  <p className="text-gray-300 max-w-md mx-auto">
                    You need to get a dedicated Miadesk phone number first before you can forward calls from your business number. Your dedicated number is the AI-powered line that answers and handles calls.
                  </p>
                  <div className="bg-white/5 rounded-lg p-4 max-w-sm mx-auto text-left space-y-2">
                    <p className="text-sm text-gray-300 font-medium">How it works:</p>
                    <div className="flex items-start gap-2 text-sm text-gray-400">
                      <span className="text-purple-400 font-bold">1.</span>
                      <span>Get a dedicated Miadesk number (included free)</span>
                    </div>
                    <div className="flex items-start gap-2 text-sm text-gray-400">
                      <span className="text-purple-400 font-bold">2.</span>
                      <span>Add your business number here</span>
                    </div>
                    <div className="flex items-start gap-2 text-sm text-gray-400">
                      <span className="text-purple-400 font-bold">3.</span>
                      <span>Set up forwarding from your business number to your Miadesk number</span>
                    </div>
                  </div>
                  <Button
                    onClick={() => setActiveTab('buy')}
                    className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white px-8 py-3"
                    data-testid="go-get-number-btn"
                  >
                    <Phone className="w-4 h-4 mr-2" /> Get Your Dedicated Number First
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <Card className="bg-gradient-to-br from-teal-900/20 to-purple-900/20 border-white/10">
                <CardHeader>
                  <CardTitle className="text-white">Add Existing Business Number</CardTitle>
                  <CardDescription className="text-gray-400">
                    Forward calls from your existing business phone to your Miadesk AI number
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="p-3 bg-emerald-500/10 rounded-lg border border-emerald-500/20 mb-2">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-emerald-400" />
                      <span className="text-sm text-emerald-300">Your Miadesk AI number: <strong>{myNumbers.find(n => n.is_twilio_number)?.phone_number}</strong></span>
                    </div>
                    <p className="text-xs text-gray-400 mt-1 ml-6">Forward your business calls to this number so Mia can answer them.</p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="existingNumber" className="text-gray-200">Your Business Phone Number</Label>
                    <Input
                      id="existingNumber"
                      value={existingNumber}
                      onChange={(e) => setExistingNumber(e.target.value)}
                      placeholder="+1 (555) 123-4567"
                      className="bg-black/30 border-white/10 text-white placeholder:text-gray-500"
                      data-testid="existing-number-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="existingNumberName" className="text-gray-200">Friendly Name (Optional)</Label>
                    <Input
                      id="existingNumberName"
                      value={existingNumberName}
                      onChange={(e) => setExistingNumberName(e.target.value)}
                      placeholder="Main Business Line"
                      className="bg-black/30 border-white/10 text-white placeholder:text-gray-500"
                      data-testid="existing-number-name-input"
                    />
                  </div>
                  <Button
                    onClick={addExistingNumber}
                    disabled={loading}
                    className="w-full bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-700 hover:to-purple-600 text-white"
                    data-testid="add-existing-number-btn"
                  >
                    {loading ? 'Adding...' : 'Add Number'}
                  </Button>
                  <div className="mt-6">
                    <CallForwardingSetup miadeskNumber={myNumbers.find(n => n.is_twilio_number)?.phone_number || ''} />
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default PhoneSetup;
